

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auth } from '../../firebase';
import WorkspaceSidebar from './WorkspaceSidebar';
import "../../css/Document.css";
import "../../css/DocumentationMainPage.css";
import "../../css/DocumentationHomeComponent.css";
import "../../css/TeamWorkspaceMainPage.css";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"
import "../../css/QuillOverrides.css";
import "../../css/TaskReference.css";
import "../../css/DocViewerMakeover.css";

function TeamWorkspaceWalkthrough() {
    const { customDomain, walkthroughId } = useParams();
    const [workspace, setWorkspace] = useState(null);
    const [walkthrough, setWalkthrough] = useState(null);
    const { enhancedRightClickEnabled } = useQuickLinks();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [authResolved, setAuthResolved] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Authenticating user...");
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [documents, setDocuments] = useState([]); // Holds fetched document data in order
    // Theme logic
    const [workspaceTheme, setWorkspaceTheme] = useState(null);
    const [ownerIsSubscriber, setOwnerIsSubscriber] = useState(false);
    // Sidebar toggle handler
    const handleSidebarToggle = (collapsed) => {
        setIsSidebarCollapsed(!collapsed);
    };
    const navigate = useNavigate();

    useEffect(() => {
        setLoadingMessage("Authenticating user...");
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserInfo(user);
            } else {
                setUserInfo(null);
            }
            setAuthResolved(true);
        });
        return () => unsubscribe();
    }, []);

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      url: "/login",
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      url: "/register",
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      url: "/my-notebook",
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      url: "https://www.youtube.com/@schoolofintology4062",
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      url: "https://discord.gg/DYkESHwgVA",
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];


const loggedInOptions = userInfo && userInfo.uid ? [
            {
                label: "Home",
                icon: <i className="fas fa-home"></i>,
                url: "/",
                onClick: () => navigate("/"),
            },
    {
        label: "Your Documents",
        url: "/documentation",
        onClick: () => navigate("/documentation"),
    },
    {
        label: "Your Walkthroughs",
        url: "/walkthroughs",
        onClick: () => navigate("/walkthroughs"),
    },
    {
        label: "Your Notebook",
        url: "/my-notebook",
        onClick: () => navigate("/my-notebook"),
    },
    {
        label: "Your Workspace",
        url: "/workspaces",
        onClick: () => navigate("/workspaces"),
    },
    {
        label: "Account Settings",
        url: `/account/${userInfo.uid}`,
        onClick: () => navigate(`/account/${userInfo.uid}`),
    },
] : [];

// Use loggedInOptions if signed in, otherwise loggedOutOptions
const menuOptions = userInfo && userInfo.uid ? loggedInOptions : loggedOutOptions;
const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    useEffect(() => {
        if (!authResolved) return;
        if (!userInfo) {
            setError('You must be logged in to view this walkthrough');
            setLoading(false);
            return;
        }

        const abortController = new AbortController();
        const signal = abortController.signal;

        const fetchWorkspaceByCustomDomain = async () => {
            setLoadingMessage("Fetching workspace details...");
            try {
                const token = await userInfo.getIdToken();
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${customDomain}/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    },
                    signal: signal
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch workspace');
                }
                const workspaceData = await response.json();
                setWorkspace(workspaceData);
                setWorkspaceTheme(workspaceData.theme);
                setOwnerIsSubscriber(workspaceData.owner?.is_subscriber);
            } catch (err) {
                setError('Error fetching workspace: ' + err.message);
                setLoading(false);
            }
        };

        if (customDomain && userInfo) {
            fetchWorkspaceByCustomDomain();
        }

        return () => {
            abortController.abort();
        };
    }, [customDomain, authResolved, userInfo]);

    // Theme application logic (copied from WorkspaceDocument.js)



    useEffect(() => {
        const fetchWalkthrough = async () => {
            setLoadingMessage("Fetching walkthrough details...");
            if (!workspace || !workspace.id) return;
            setLoading(true);
            setError(null);
            try {
                const token = await userInfo.getIdToken();
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/walkthroughs/${walkthroughId}/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `${token}`,
                        'Auth-Token': `Bearer ${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    }
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch walkthrough');
                }
                const data = await response.json();
                setWalkthrough(data);
            } catch (err) {
                setError('Error fetching walkthrough: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        if (workspace && walkthroughId && userInfo) {
            fetchWalkthrough();
        }
    }, [workspace, walkthroughId, userInfo]);

    // Fetch all documents in the walkthrough by ID (space or page)
    const fetchDocumentsByOrder = useCallback(async () => {
        if (!walkthrough || !walkthrough.document_order || !workspace || !userInfo) {
            setDocuments([]);
            return;
        }
        setLoading(true);
        setLoadingMessage("Fetching documents in order...");
        setError(null);
        try {
            const token = await userInfo.getIdToken();
            const docPromises = walkthrough.document_order.map(async (docKey) => {
                // Accept both 'space-27' and 'space:27' formats
                let type = null;
                let id = null;
                if (docKey.startsWith('space:') || docKey.startsWith('space-')) {
                    type = 'space';
                    id = docKey.replace('space:', '').replace('space-', '');
                } else if (docKey.startsWith('page:') || docKey.startsWith('page-')) {
                    type = 'page';
                    id = docKey.replace('page:', '').replace('page-', '');
                } else {
                    return { error: 'Unknown document type', docKey };
                }
                let url = '';
                if (type === 'space') {
                    url = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${id}/`;
                } else if (type === 'page') {
                    url = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${id}/`;
                }
                const resp = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    }
                });
                if (!resp.ok) {
                    return { error: `Failed to fetch document: ${docKey}`, docKey };
                }
                const docData = await resp.json();
                return { ...docData, docKey, type };
            });
            const docs = await Promise.all(docPromises);
            setDocuments(docs);
        } catch (err) {
            setError('Error fetching documents: ' + err.message);
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    }, [walkthrough, workspace, userInfo]);

    // Refetch documents when walkthrough changes
    useEffect(() => {
        if (walkthrough && walkthrough.document_order && workspace && userInfo) {
            fetchDocumentsByOrder();
        } else {
            setDocuments([]);
        }
    }, [walkthrough, workspace, userInfo, fetchDocumentsByOrder]);

    // Helper to format timestamps
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    // Navigation handlers
    const handleNextDocument = () => {
        if (walkthrough && walkthrough.document_order && currentDocumentIndex < walkthrough.document_order.length) {
            setCurrentDocumentIndex(currentDocumentIndex + 1);
        }
    };

    const handlePreviousDocument = () => {
        if (currentDocumentIndex > 0) {
            setCurrentDocumentIndex(currentDocumentIndex - 1);
        }
    };

    // Delete walkthrough handler
    const handleDeleteWalkthrough = async () => {
        if (!walkthrough || !workspace || !userInfo) return;
        if (!window.confirm('Are you sure you want to delete this walkthrough? This action cannot be undone.')) return;
        try {
            setLoading(true);
            const token = await userInfo.getIdToken();
            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/walkthroughs/${walkthroughId}/delete/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Auth-Token': `Bearer ${token}`,
                    'Authorization': `${token}`,
                    'Requesting-User-Uid': userInfo.uid
                }
            });
            if (!response.ok) {
                throw new Error('Failed to delete walkthrough');
            }
            // Navigate to workspace main page after deletion
            navigate(`/workspace/${customDomain}`);
        } catch (err) {
            setError('Error deleting walkthrough: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleHomeButtonClick = () => { navigate("/") }

    if (loading) {
    return (
      <div className="DocumentationHomeComponent__coffee-loader-div">
        <div className="coffee-cup-loader">
          <div className="cup">
            <div className="cup-body"></div>
            <div className="cup-handle"></div>
            <div className="cup-base"></div>
            <div className="cup-shadow"></div>
            <div className="steam steam1"></div>
            <div className="steam steam2"></div>
            <div className="steam steam3"></div>
          </div>
        </div>
        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
      </div>
    );
    }

  if (error) {
      return (
          <div 
            className="DocumentationMainPage__div DocViewer DocViewer--ws"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
              <div className="DocumentationMainPage__main-content">
                  <p className="large">Oops! Something went wrong!</p>
                  <p className="medium">{error}</p>
                  <button onClick={handleHomeButtonClick} className="ConflictResolver__button">Home</button>
              </div>
              {enhancedRightClickEnabled && ContextMenuComponent}
          </div>
      );
  }

    if (authResolved && !userInfo) {
        return (
            <div 
                className="DocumentationMainPage__div DocViewer DocViewer--ws"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
            >
                <div className="DocumentationMainPage__main-content">
                    <h3>Unauthorized</h3>
                    <p>Please log in to view this document.</p>
                    <button onClick={() => navigate("/login")} className="ConflictResolver__button">Log In</button>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    return (
        <div 
            className="TeamWorkspaceMainPage__div DocViewer DocViewer--ws"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            <WorkspaceSidebar
                workspaceId={workspace?.id}
                customDomain={customDomain}
                workspaceTheme={workspace?.theme}
                destinationTab="walkthroughs"
                canCreate={true}
                onToggleSidebar={handleSidebarToggle}
            />
            {/* Floating overlay for status and edit actions (WorkspaceDocument.js style) */}
            {walkthrough && !loading && !error && (
                <div className="Document__floating-div">
                    {/* Edit button (show lock if not allowed) */}
                    {userInfo && workspace && workspace.user_role !== 'VIEWER' ? (
                        <Link
                            to={`/workspace/${customDomain}/edit/walkthrough/${walkthroughId}`}
                            className="Document__a"
                        >
                            <i className="fas fa-edit Document__icon-padding Document__icon-hover" title="Edit" style={{ marginRight: "8px", marginLeft: "8px", cursor: "pointer" }}></i>
                        </Link>
                    ) : (
                        <i className="fas fa-lock Document__icon-padding" title="Can't Edit" style={{ marginRight: "8px", marginLeft: "8px", cursor: "not-allowed" }}></i>
                    )}

                    {/* Delete button (show lock if not allowed) */}
                    {userInfo && workspace && workspace.user_role !== 'VIEWER' ? (
                        <i className="fas fa-trash Document__icon-padding Document__icon-hover" title="Delete Walkthrough" style={{ marginRight: "8px", cursor: "pointer" }} onClick={handleDeleteWalkthrough}></i>
                    ) : (
                        <i className="fas fa-lock Document__icon-padding" title="Can't Delete" style={{ marginRight: "8px", cursor: "not-allowed" }}></i>
                    )}

                    <div className="Document__icon-separator"></div>

                    {/* {walkthrough.is_public ? (
                        <i className="fas fa-eye Document__icon-padding" title="Walkthrough is Public" style={{ cursor: "pointer" }}></i>
                    ) : (
                        <i className="fas fa-eye-slash Document__icon-padding" title="Walkthrough is not Public" style={{ cursor: "pointer" }}></i>
                    )} */}

                    {/* Published/Not Published status */}
                    {walkthrough.completed ? (
                        <i className="fa-solid fa-circle-check Document__icon-padding" title="Published"></i>
                    ) : (
                        <i className="fa-solid fa-circle-xmark Document__icon-padding" title="Not Published"></i>
                    )}
                </div>
            )}
            {walkthrough && !loading && !error && (
                <div className="TeamWorkspaceMainPage__div DocViewer DocViewer--ws WalkthroughPage__doc-wrap">
                    {currentDocumentIndex === 0 ? (
                        <div className="WalkthroughPage__main-content">
                            <h2>{walkthrough.walkthrough_name}</h2>
                            {/* Author info if available */}
                            {walkthrough.author_name && (
                                <p className="medium WalkthroughPage__author-name">By: {walkthrough.author_name}</p>
                            )}
                            <div className="ql-editor ql-editor-styling WalkthroughPage__tutorial-cover" dangerouslySetInnerHTML={{ __html: walkthrough.tutorial_cover || '' }} />
                            <p className="extra-small" style={{ paddingTop: "30px" }}>
                                <span className="semi-bold">Created At:</span> <i>{formatTimestamp(walkthrough.created_at)}</i>
                            </p>
                            <p className="extra-small" style={{ paddingBottom: "30px" }}>
                                <span className="semi-bold">Last Updated At:</span> <i>{formatTimestamp(walkthrough.updated_at)}</i>
                            </p>
                        </div>
                    ) : (
                        <div className="TeamWorkspaceMainPage__main-content">
                            <div className="Document__div">
                                {(() => {
                                    const doc = documents[currentDocumentIndex - 1];
                                    if (!doc) {
                                        return <p>Loading document...</p>;
                                    }
                                    if (doc.error) {
                                        return <p style={{color: 'red'}}>Error loading document: {doc.docKey}</p>;
                                    }
                                    return (
                                        <>
                                            <h2 style={{ marginBottom: "30px" }}>{doc.page_name || doc.name}</h2>
                                            {doc.type === 'page' && doc.content && (
                                                <div className="ql-editor ql-editor-styling" dangerouslySetInnerHTML={{ __html: doc.content }} />
                                            )}
                                            {doc.type === 'space' && doc.content && (
                                                <div className="ql-editor ql-editor-styling" dangerouslySetInnerHTML={{ __html: doc.content }} />
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Navigation arrows */}
                    {currentDocumentIndex > 0 && (
                        <button
                            className="WalkthroughPage__prev-button"
                            onClick={handlePreviousDocument}
                            style={{ left: isSidebarCollapsed ? '15px' : '310px' }}
                        >
                            <i className="fa-solid fa-arrow-left walkthrough-navigation-arrow"></i>
                        </button>
                    )}

                    {walkthrough.document_order && currentDocumentIndex < walkthrough.document_order.length && (
                        <button
                            className="WalkthroughPage__next-button"
                            onClick={handleNextDocument}
                        >
                            <i className="fa-solid fa-arrow-right walkthrough-navigation-arrow"></i>
                        </button>
                    )}
                </div>
            )}
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );

}

export default TeamWorkspaceWalkthrough;